import { useEffect } from "react";
import { FileText, AlertCircle, Scale, Building, Users, CheckCircle } from "lucide-react";

function TermsConditionsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-brand-navy to-blue-900 py-16 sm:py-20 md:py-24 lg:py-20">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-white/10 p-4 rounded-full">
                <FileText className="w-12 h-12 sm:w-16 sm:h-16 text-white" />
              </div>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-4 sm:mb-6">
              Terms & Conditions
            </h1>
            <p className="font-body text-lg sm:text-xl text-white/90 leading-relaxed max-w-2xl mx-auto">
              Please read these terms and conditions carefully before using our services.
            </p>
            <p className="text-white/70 mt-4">Last Updated: October 28, 2025</p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="bg-white py-12 sm:py-16 md:py-20 lg:py-16">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="max-w-4xl mx-auto">
            
            {/* Introduction */}
            <div className="mb-12">
              <p className="text-text-dark text-base sm:text-lg leading-relaxed">
                Welcome to IMMACULATE (I.H.C.L.). These Terms and Conditions govern your use of our services and website. 
                By accessing or using our services, you agree to be bound by these terms. If you disagree with any part of 
                these terms, you may not access our services.
              </p>
            </div>

            {/* Acceptance of Terms */}
            <div className="mb-12">
              <div className="flex items-start mb-4">
                <CheckCircle className="w-8 h-8 text-gold mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                    1. Acceptance of Terms
                  </h2>
                </div>
              </div>
              <div className="pl-12 space-y-3 text-text-dark">
                <p>
                  By using our services, you confirm that you accept these Terms and Conditions and that you agree to comply 
                  with them. These terms apply to all visitors, users, candidates, clients, and others who access our services.
                </p>
              </div>
            </div>

            {/* Services Description */}
            <div className="mb-12">
              <div className="flex items-start mb-4">
                <Building className="w-8 h-8 text-gold mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                    2. Services Description
                  </h2>
                </div>
              </div>
              <div className="pl-12 space-y-3 text-text-dark">
                <p>IMMACULATE provides manpower and staffing solutions, including but not limited to:</p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Recruitment and placement services</li>
                  <li>Temporary and permanent staffing</li>
                  <li>Workforce management</li>
                  <li>HR consulting and support</li>
                  <li>Payroll processing and administration</li>
                  <li>Compliance and documentation services</li>
                </ul>
                <p className="mt-4">
                  We reserve the right to modify, suspend, or discontinue any part of our services at any time without prior notice.
                </p>
              </div>
            </div>

            {/* Client Obligations */}
            <div className="mb-12">
              <div className="flex items-start mb-4">
                <Users className="w-8 h-8 text-gold mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                    3. Client Obligations
                  </h2>
                </div>
              </div>
              <div className="pl-12 space-y-3 text-text-dark">
                <p>Clients agree to:</p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Provide accurate and complete information about job requirements</li>
                  <li>Comply with Indian labor laws and regulations</li>
                  <li>Pay agreed fees and charges on time</li>
                  <li>Provide a safe working environment for placed staff</li>
                  <li>Not directly hire or solicit our candidates without written agreement</li>
                  <li>Report any issues or concerns promptly</li>
                  <li>Maintain confidentiality of candidate information</li>
                </ul>
              </div>
            </div>

            {/* Candidate Obligations */}
            <div className="mb-12">
              <div className="flex items-start mb-4">
                <Users className="w-8 h-8 text-gold mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                    4. Candidate Obligations
                  </h2>
                </div>
              </div>
              <div className="pl-12 space-y-3 text-text-dark">
                <p>Candidates agree to:</p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Provide truthful and accurate information in applications</li>
                  <li>Maintain valid work authorization and documentation</li>
                  <li>Comply with client company policies and procedures</li>
                  <li>Perform duties professionally and diligently</li>
                  <li>Report to work as scheduled and on time</li>
                  <li>Notify us immediately of any issues or changes in availability</li>
                  <li>Not disclose confidential information</li>
                </ul>
              </div>
            </div>

            {/* Fees and Payment */}
            <div className="mb-12">
              <div className="flex items-start mb-4">
                <Scale className="w-8 h-8 text-gold mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                    5. Fees and Payment
                  </h2>
                </div>
              </div>
              <div className="pl-12 space-y-3 text-text-dark">
                <ul className="space-y-2 list-disc list-inside">
                  <li>Service fees are agreed upon in writing before service commencement</li>
                  <li>Payment terms are typically NET 30 days unless otherwise agreed</li>
                  <li>Late payments may incur interest charges as per Indian commercial law</li>
                  <li>Candidates are not charged for placement services</li>
                  <li>All fees are subject to applicable GST and taxes</li>
                  <li>Refund policies are outlined in individual service agreements</li>
                </ul>
              </div>
            </div>

            {/* Intellectual Property */}
            <div className="mb-12">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                6. Intellectual Property Rights
              </h2>
              <div className="text-text-dark space-y-3">
                <p>
                  The website content, including text, graphics, logos, images, and software, is the property of IMMACULATE 
                  and is protected by Indian and international copyright laws. You may not reproduce, distribute, or create 
                  derivative works without our express written permission.
                </p>
              </div>
            </div>

            {/* Confidentiality */}
            <div className="mb-12">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                7. Confidentiality
              </h2>
              <div className="text-text-dark space-y-3">
                <p>
                  All parties agree to maintain confidentiality of any proprietary or sensitive information shared during 
                  the course of our business relationship. This obligation continues even after termination of services.
                </p>
              </div>
            </div>

            {/* Limitation of Liability */}
            <div className="mb-12">
              <div className="flex items-start mb-4">
                <AlertCircle className="w-8 h-8 text-gold mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                    8. Limitation of Liability
                  </h2>
                </div>
              </div>
              <div className="pl-12 space-y-3 text-text-dark">
                <p>
                  IMMACULATE shall not be liable for any indirect, incidental, special, consequential, or punitive damages 
                  resulting from your use of our services. Our total liability shall not exceed the fees paid for the specific 
                  service giving rise to the claim.
                </p>
                <p className="mt-4">We are not responsible for:</p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Candidate performance or conduct after placement</li>
                  <li>Client workplace conditions or practices</li>
                  <li>Third-party actions or omissions</li>
                  <li>Force majeure events beyond our control</li>
                </ul>
              </div>
            </div>

            {/* Termination */}
            <div className="mb-12">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                9. Termination
              </h2>
              <div className="text-text-dark space-y-3">
                <p>
                  Either party may terminate services with written notice as specified in the service agreement. We reserve 
                  the right to terminate services immediately for breach of these terms or applicable laws. Outstanding 
                  obligations survive termination.
                </p>
              </div>
            </div>

            {/* Governing Law */}
            <div className="mb-12">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                10. Governing Law and Jurisdiction
              </h2>
              <div className="text-text-dark space-y-3">
                <p>
                  These Terms and Conditions are governed by the laws of India. Any disputes arising from 
                  these terms shall be subject to the exclusive jurisdiction of the courts of Mumbai, Maharashtra, India.
                </p>
              </div>
            </div>

            {/* Dispute Resolution */}
            <div className="mb-12">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                11. Dispute Resolution
              </h2>
              <div className="text-text-dark space-y-3">
                <p>
                  In the event of any dispute, the parties agree to first attempt resolution through good faith negotiations. 
                  If negotiations fail, disputes may be referred to mediation before pursuing legal action.
                </p>
              </div>
            </div>

            {/* Amendments */}
            <div className="mb-12">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                12. Amendments
              </h2>
              <div className="text-text-dark space-y-3">
                <p>
                  We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately 
                  upon posting to our website. Your continued use of our services constitutes acceptance of modified terms.
                </p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gray-50 p-6 sm:p-8 rounded-lg border-l-4 border-gold">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                Contact Us
              </h2>
              <div className="text-text-dark space-y-2">
                <p>If you have any questions about these Terms and Conditions, please contact us:</p>
                <p className="font-semibold mt-4">IMMACULATE (I.H.C.L.)</p>
                <p>Email: info@immaculate.ltd</p>
                <p>Phone: +91 99873 43532</p>
                <p>Address: Navi Mumbai 400703, Maharashtra, India</p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}

export default TermsConditionsPage;

